import React, { useCallback } from 'react';

import { Checkbox, Tooltip } from '@primer/react';

import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';

import byteSize from 'byte-size';

import Label from '../../../common/Labels/Label';
import LinkButton from '../../../common/Labels/LinkButton';

import { formatDateWithTimeSeparator } from '../../../../utils/date_utils';
import { getThemeColor } from '../../../../utils/theme-utils';
import NoVersionsSlate from '../../pipelines/AdminPipelineHistoryModal/NoVersionsSlate';

const VersionRow = ({
  index,
  style,
  data,
}) => {
  const {
    values,
    onItemSelected,
    isValueSelected,
  } = data;

  const row = values[index];
  const version = row?.model_version?.file_info?.version;
  const storageSize = row?.model_version?.file_info?.size;
  const storageData = byteSize(storageSize);
  const storageLabel = `${storageData.value} ${storageData.unit}`;
  const isSelected = isValueSelected(version);
  const isDeployed = row?.model_version?.deployed;

  const onClick = useCallback((e) => {
    e.stopPropagation();
    if (!isDeployed) {
      onItemSelected(version);
    }
  }, [isDeployed, onItemSelected, version]);

  return (
    <div
      id={`model-version-entry-${index}`}
      className="flex cursor-pointer flex items-center bg-white hover:bg-border-light w-full"
      style={style}
      onClick={onClick}
    >
      <div className="flex items-center justify-center w-full">
        <Checkbox
          id={`selection-checkbox-${version}`}
          checked={isSelected}
          onChange={() => onItemSelected(version)}
          disabled={isDeployed}
        />
        <div className="ml-4 flex justify-between w-full">
          <Label
            className="col-span-2"
            style={{ color: isDeployed ? getThemeColor('info') : getThemeColor('brand') }}
          >
            {version}
          </Label>
          <div className="flex justify-end">
            <Label
              style={{ color: isDeployed ? getThemeColor('info') : getThemeColor('brand') }}
            >
              {storageLabel}
            </Label>
            <Label
              className="ml-4"
              style={{ color: isDeployed ? getThemeColor('info') : getThemeColor('brand') }}
            >
              {formatDateWithTimeSeparator(row?.model_version?.created_at, ' at ', '--')}
            </Label>
          </div>
        </div>
        {
          isDeployed && (
            <Tooltip aria-label="Unable to delete a model in a deployed pipeline version">
              <div className="z-16 absolute w-20 h-8 -left-10 -top-2" />
            </Tooltip>
          )
        }
      </div>
    </div>
  )
}

export const SelectableModelHistoryList = ({
  listRef,
  values,
  selectedValues = [],
  setSelectedValues = () => {},
  height = 480,
}) => {
  const onSelectAll = () => setSelectedValues(values.filter(({ deployed }) => !deployed).map(({ model_version }) => model_version));

  const onDeselectAll = () => setSelectedValues([]);

  const isValueSelected = selectedVersion => !!selectedValues.find(version => version === selectedVersion)

  const onItemSelected = (selectedVersion) => {
    const isSelected = isValueSelected(selectedVersion);
    if (isSelected) {
      setSelectedValues(selectedValues.filter(version => version !== selectedVersion))
    } else {
      setSelectedValues([
        ...selectedValues,
        selectedVersion,
      ])
    }
  }
  return (
    <div className="m-1">
      <div className="mx-1 my-1 flex">
        <LinkButton
          className="mr-4"
          size="sm"
          color="link-blue"
          hoverColor="faded-brand"
          activeColor="highlighted"
          onClick={onSelectAll}
          showUnderlineOnHover
        >
          Select All
        </LinkButton>
        <LinkButton
          size="sm"
          color="link-red"
          hoverColor="faded-error"
          activeColor="error"
          onClick={onDeselectAll}
          showUnderlineOnHover
        >
          Deselect All
        </LinkButton>
      </div>
      <div className="w-full">
        {
          values?.length > 0 ? (
            <AutoSizer disableHeight>
              {({ width }) => (
                <FixedSizeList
                  ref={listRef}
                  height={height}
                  itemCount={values.length}
                  itemSize={32}
                  width={width}
                  itemData={{
                    values,
                    isValueSelected,
                    onItemSelected,
                  }}
                >
                  {VersionRow}
                </FixedSizeList>
              )}
            </AutoSizer>
          ) : (
            <div className="mt-8">
              <NoVersionsSlate
                mainText="We couldn't find any versions for this model."
              />
            </div>
          )
        }
      </div>
    </div>
  )
}

export default SelectableModelHistoryList;
